import { useState, useEffect, useCallback } from "react"
import { Loader2, Plus, Target, X, Check, Medal } from "lucide-react"
import SwimmerSidebar from "../components/SwimmerSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const GOAL_ICONS = ["⏱", "🏊", "🏆", "🔥", "📈", "🎯"]

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function SwimmerGoals() {
  const [goals, setGoals] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ title: "", current: "", target: "", progress: 0, icon: GOAL_ICONS[0] })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  
  const [completionModalGoal, setCompletionModalGoal] = useState(null)
  const [isCompleting, setIsCompleting] = useState(false)

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  const fetchGoals = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetch(`${API}/api/me/goals`, { headers: authHeaders() })
      if (!res.ok) throw new Error("Failed to fetch goals")
      setGoals(await res.json())
    } catch (e) {
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchGoals()
  }, [fetchGoals])

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitError("")
    setIsSubmitting(true)
    try {
      const res = await fetch(`${API}/api/me/goals`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          title: formData.title,
          current: formData.current,
          target: formData.target,
          progress: Number(formData.progress),
          icon: formData.icon,
          completed: false,
        })
      })
      if (!res.ok) throw new Error("Save failed")
      
      setShowModal(false)
      setFormData({ title: "", current: "", target: "", progress: 0, icon: GOAL_ICONS[0] })
      fetchGoals()
    } catch (err) {
      setSubmitError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleProgressChange = (id, newProgress) => {
    setGoals(prev => prev.map(g => g.id === id ? { ...g, progress: newProgress } : g))
  }
  
  const handleProgressRelease = async (goal) => {
    try {
      await fetch(`${API}/api/me/goals/${goal.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          progress: goal.progress,
          completed: goal.completed,
        })
      })
      
      if (goal.progress === 100 && !goal.completed) {
        setCompletionModalGoal(goal)
      }
    } catch (err) {
      console.error("Failed to update progress:", err)
    }
  }

  const confirmCompletion = async () => {
    if (!completionModalGoal) return
    setIsCompleting(true)
    try {
      const res = await fetch(`${API}/api/me/goals/${completionModalGoal.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          progress: 100,
          completed: true,
        })
      })
      if (!res.ok) throw new Error("Update failed")
      
      setGoals(prev => prev.map(g => g.id === completionModalGoal.id ? { ...g, progress: 100, completed: true } : g))
      setCompletionModalGoal(null)
    } catch (err) {
      console.error(err)
    } finally {
      setIsCompleting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10 relative">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("goals_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("goals_subtitle")}</p>
          </div>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 rounded-lg bg-gold-500 px-4 py-2 text-sm font-bold text-ink-900 hover:bg-gold-400 transition-colors">
            <Plus className="h-4 w-4" /> {t("goals_add")}
          </button>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {goals.map(g => (
              <div key={g.id} className="flex flex-col justify-between rounded-xl border border-white/10 bg-ink-800/30 p-6 shadow-sm relative overflow-hidden">
                {g.completed && (
                  <div className="absolute top-0 right-0 rounded-bl-xl bg-gold-500/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-400 backdrop-blur-sm border-b border-l border-gold-500/30 flex items-center gap-1">
                    <Medal className="h-3 w-3" /> {t("goals_completed")}
                  </div>
                )}
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-2xl">{g.icon}</span>
                    {!g.completed && <Target className="h-4 w-4 text-mist-500" />}
                  </div>
                  <h3 className="font-display text-lg font-bold text-paper-0">{g.title}</h3>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-mist-500">{t("goals_current")}</p>
                      <p className="font-semibold text-mist-300">{g.current}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-mist-500">{t("goals_target")}</p>
                      <p className="font-semibold text-gold-400">{g.target}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <div className="mb-2 flex items-center justify-between text-[10px] font-bold uppercase text-mist-400">
                    <span>{t("goals_progress")}</span>
                    <span>{g.progress}%</span>
                  </div>
                  {g.completed ? (
                    <div className="h-2 w-full overflow-hidden rounded-full bg-ink-900 border border-white/5">
                      <div className="h-full rounded-full bg-emerald-500/80" style={{ width: '100%' }} />
                    </div>
                  ) : (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={g.progress}
                      onChange={(e) => handleProgressChange(g.id, parseInt(e.target.value))}
                      onMouseUp={() => handleProgressRelease(g)}
                      onTouchEnd={() => handleProgressRelease(g)}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer focus:outline-none"
                      style={{
                        background: `linear-gradient(to right, #C6A87C ${g.progress}%, #1E293B ${g.progress}%)`,
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
            {goals.length === 0 && (
              <div className="col-span-full">
                <EmptyState
                  icon={<Target className="h-12 w-12 text-mist-600" />}
                  title={t("goals_empty")}
                  hint={t("goals_empty_hint")}
                />
              </div>
            )}
          </div>
        )}
      </main>

      {/* New Goal Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-ink-800 p-8 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-paper-0">{t("goals_create")}</h2>
              <button onClick={() => setShowModal(false)} className="text-mist-400 hover:text-paper-0"><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">Title</label>
                <input required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g. Sub-60s 100m Free" className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">Current</label>
                  <input required value={formData.current} onChange={e => setFormData({...formData, current: e.target.value})} placeholder="e.g. 1:05.40" className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">Target</label>
                  <input required value={formData.target} onChange={e => setFormData({...formData, target: e.target.value})} placeholder="e.g. 0:59.99" className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("goals_progress")} (%)</label>
                  <input required type="number" min="0" max="100" value={formData.progress} onChange={e => setFormData({...formData, progress: e.target.value})} className="w-full rounded border border-white/10 bg-ink-900/50 p-3 text-sm text-paper-0 focus:border-gold-500 focus:outline-none" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-mist-500">{t("goals_icon_label")}</label>
                  <div className="flex gap-2 flex-wrap">
                    {GOAL_ICONS.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        aria-label={`Use ${icon} icon`}
                        aria-pressed={formData.icon === icon}
                        className={`flex h-10 w-10 items-center justify-center rounded border text-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 ${formData.icon === icon ? "border-gold-500 bg-gold-500/10" : "border-white/10 bg-ink-900/50 hover:border-white/25"}`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {submitError && <p className="text-sm font-medium text-red-300">{submitError}</p>}

              <div className="mt-4 flex justify-end gap-3 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setShowModal(false)} className="rounded px-4 py-2 text-sm font-semibold text-mist-300 hover:text-paper-0">{t("goals_cancel")}</button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 rounded bg-gold-500 px-6 py-2 text-sm font-bold text-ink-900 hover:bg-gold-400 disabled:opacity-50">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {t("goals_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Completion Prompt Modal */}
      {completionModalGoal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-gold-500/30 bg-ink-800 p-8 shadow-2xl text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/20 text-3xl shadow-inner border border-gold-500/40">
              {completionModalGoal.icon}
            </div>
            <h2 className="font-display text-2xl font-bold text-paper-0 mb-2">{t("goals_reached")}</h2>
            <p className="text-sm text-mist-300 mb-6">
              {t("goals_reached_msg")} <strong>{completionModalGoal.title}</strong>. {t("goals_reached_msg2")}
            </p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={confirmCompletion} 
                disabled={isCompleting}
                className="flex items-center justify-center gap-2 rounded bg-gold-500 px-4 py-3 text-sm font-bold text-ink-900 hover:bg-gold-400 transition-colors disabled:opacity-50"
              >
                {isCompleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {t("goals_yes")}
              </button>
              <button 
                onClick={() => setCompletionModalGoal(null)} 
                disabled={isCompleting}
                className="rounded border border-white/10 bg-transparent px-4 py-3 text-sm font-semibold text-mist-300 hover:text-paper-0 hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                {t("goals_not_yet")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
